import { useEffect, useState } from "react";
import { PageSkeleton } from "@/components/PageSkeleton";
import api from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Clock, Users2, Building2 } from "lucide-react";

const badgeFor = (status) =>
  status === "overdue"
    ? "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300"
    : "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300";

const label = (r) => (r.status === "overdue" ? `Telat ${Math.abs(r.days)} hari` : r.days === 0 ? "Jatuh tempo hari ini" : `${r.days} hari lagi`);

export default function Reminders() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reminders").then((r) => setData(r.data)); }, []);
  if (!data) return <PageSkeleton stats={2} rows={4} />;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Pengingat Jatuh Tempo</h2>
        <p className="text-xs sm:text-sm text-muted-foreground">Tagihan pelanggan & supplier yang mendekati atau melewati jatuh tempo.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 max-w-md">
        <Card data-testid="reminder-overdue" className="p-4">
          <div className="flex items-center gap-2.5">
            <div className="grid place-items-center h-9 w-9 rounded-lg bg-rose-100 dark:bg-rose-950 shrink-0"><AlertTriangle className="h-4 w-4 text-rose-600" /></div>
            <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">Lewat Tempo</p><p className="font-heading text-xl font-extrabold">{data.overdue_count}</p></div>
          </div>
        </Card>
        <Card data-testid="reminder-soon" className="p-4">
          <div className="flex items-center gap-2.5">
            <div className="grid place-items-center h-9 w-9 rounded-lg bg-amber-100 dark:bg-amber-950 shrink-0"><Clock className="h-4 w-4 text-amber-600" /></div>
            <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">≤ 7 Hari</p><p className="font-heading text-xl font-extrabold">{data.soon_count}</p></div>
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="px-4 pt-4 flex items-center gap-2"><Users2 className="h-4 w-4 text-primary" /><h3 className="font-heading text-sm font-semibold">Piutang Pelanggan</h3></div>
        <div className="hidden lg:block mt-2">
          <Table>
            <TableHeader><TableRow><TableHead>Pelanggan</TableHead><TableHead>Kategori</TableHead><TableHead>Jatuh Tempo</TableHead><TableHead className="text-right">Sisa Tagihan</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.customer.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Tidak ada pengingat pelanggan.</TableCell></TableRow>}
              {data.customer.map((r) => (
                <TableRow key={r.id} data-testid={`reminder-cust-${r.id}`}>
                  <TableCell className="font-medium">{r.name}{r.phone ? <span className="text-xs text-muted-foreground"> · {r.phone}</span> : ""}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.category}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{formatDate(r.due_date)}</TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap">{formatIDR(r.remaining)}</TableCell>
                  <TableCell><Badge variant="outline" className={badgeFor(r.status)}>{label(r)}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="lg:hidden divide-y divide-border mt-2">
          {data.customer.length === 0 && <p className="text-center text-muted-foreground text-xs py-6">Tidak ada pengingat pelanggan.</p>}
          {data.customer.map((r) => (
            <div key={r.id} data-testid={`reminder-cust-${r.id}`} className="p-3 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{r.name}{r.phone ? <span className="text-[11px] text-muted-foreground font-normal"> · {r.phone}</span> : ""}</p>
                  <p className="text-[11px] text-muted-foreground">{r.category} · Tempo {formatDate(r.due_date)}</p>
                </div>
                <Badge variant="outline" className={`${badgeFor(r.status)} text-[10px] px-1.5 py-0 shrink-0`}>{label(r)}</Badge>
              </div>
              <p className="text-xs">Sisa: <b>{formatIDR(r.remaining)}</b></p>
            </div>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="px-4 pt-4 flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /><h3 className="font-heading text-sm font-semibold">Utang Supplier</h3></div>
        <div className="hidden lg:block mt-2">
          <Table>
            <TableHeader><TableRow><TableHead>Supplier</TableHead><TableHead>No. Invoice</TableHead><TableHead>Jatuh Tempo</TableHead><TableHead className="text-right">Jumlah</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.supplier.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Tidak ada pengingat supplier.</TableCell></TableRow>}
              {data.supplier.map((r) => (
                <TableRow key={r.id} data-testid={`reminder-sup-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.invoice_no}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{formatDate(r.due_date)}</TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap">{formatIDR(r.amount)}</TableCell>
                  <TableCell><Badge variant="outline" className={badgeFor(r.status)}>{label(r)}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="lg:hidden divide-y divide-border mt-2">
          {data.supplier.length === 0 && <p className="text-center text-muted-foreground text-xs py-6">Tidak ada pengingat supplier.</p>}
          {data.supplier.map((r) => (
            <div key={r.id} data-testid={`reminder-sup-${r.id}`} className="p-3 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{r.name}</p>
                  <p className="text-[11px] text-muted-foreground">{r.invoice_no} · Tempo {formatDate(r.due_date)}</p>
                </div>
                <Badge variant="outline" className={`${badgeFor(r.status)} text-[10px] px-1.5 py-0 shrink-0`}>{label(r)}</Badge>
              </div>
              <p className="text-xs">Jumlah: <b>{formatIDR(r.amount)}</b></p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
